import { useState, useEffect, useMemo } from 'react';
import './Dashboard.css';
import { FaPowerOff } from 'react-icons/fa';
import { MdSensors } from 'react-icons/md';
import { useSystem } from '../../context/SystemContext';
import { useToast } from '../../context/ToastContext';
import { SHARED_MODE, SPECIAL_MODE, CONNECTION_STATUS, HEALTH_STATUS } from '../../context/enums';
import {
  getStatusText,
  PRIMING_SYSTEM,
  COLLABORATIVE_PRIMING,
  AUTONOMY_SUPPRESSED_LABEL,
  COLLABORATIVE_DEFENSE_LABEL,
  PRESOAKING_LABEL,
  NON_EMERGENCY_COOLDOWN_LABEL,
  isAutonomySuppressed,
  isCollaborativeDefenseEngaged,
  isPresoakingEngaged,
  isNonEmergencyCooldownActive
} from '../../utils/systemStatusUtils';
import { parseDurationToSeconds, formatPrimingCountdown, getDurationRemainingText } from '../../context/overrideDurationUtils';
import { DISPLAY_TEXT, formatTimeReported, getTimeStalenessClass } from '../../utils/timeReportedUtils';
import { usePresoakAction } from '../../hooks/usePresoakAction';
import { useActivateAction } from '../../hooks/useActivateAction';
import { useCollaborativeAction } from '../../hooks/useCollaborativeAction';
import ActivateButton from './ActivateButton';
import PresoakOrCollaborativeButton from './PresoakOrCollaborativeButton';
import ConfirmationPopup from '../../components/ConfirmationPopup';
import aifdNotSoaked from '../../assets/AIFD_Ready.png';
import aifdSoaked from '../../assets/AIFD_Active.png';
import aifdDisconnected from '../../assets/AIFD_Disconnected.png';

// Connected lifecycle states that display the system as ready and not yet soaked.
const AIFD_READY_SHARED_MODES = new Set([
  SHARED_MODE.OFF,
  SHARED_MODE.ACTIVATING,
  SHARED_MODE.PRIMING,
  SHARED_MODE.DEACTIVATING,
]);
// Connected lifecycle states that display the system as actively soaked.
const AIFD_ACTIVE_SHARED_MODES = new Set([
  SHARED_MODE.ON,
]);

/**
 * Selects the Dashboard image for recognized connected lifecycle states.
 * @param {string|null} connectionStatus - Current aggregate connection status.
 * @param {string|undefined} sharedMode - Current shared operating mode.
 * @returns {string} Imported AIFD image source.
 */
function getDashboardImageSource(connectionStatus, sharedMode) {
  if (AIFD_ACTIVE_SHARED_MODES.has(sharedMode)) {
    return aifdSoaked;
  }
  if (connectionStatus !== CONNECTION_STATUS.CONNECTED) {
    return aifdDisconnected;
  }
  if (AIFD_READY_SHARED_MODES.has(sharedMode)) {
    return aifdNotSoaked;
  }
  return aifdDisconnected;
}

/**
 * Returns the interpolated seconds remaining in a priming countdown anchored at a point in time.
 * @param {{receivedAt: number, totalSeconds: number}|null} primingAnchor - Anchor snapshot, or null if none set.
 * @returns {number|null} Seconds remaining (never negative), or null if no anchor is set.
 */
function getPrimingRemainingSeconds(primingAnchor) {
  if (!primingAnchor) return null;
  const elapsed = Math.floor((Date.now() - primingAnchor.receivedAt) / 1000);
  return Math.max(0, primingAnchor.totalSeconds - elapsed);
}

/**
 * Resolves the PRIMING-state display text from the priming anchor, or null if PRIMING
 * doesn't apply or no anchor is set.
 * @param {Object|null} systemStatus - The structured system status.
 * @param {{receivedAt: number, totalSeconds: number}|null} primingAnchor - Anchor snapshot, or null if none set.
 * @returns {string|null} Formatted "{label}: {countdown} remaining" text, or null.
 */
function getPrimingDisplayText(systemStatus, primingAnchor) {
  if (systemStatus?.sharedMode !== SHARED_MODE.PRIMING) return null;
  const remaining = getPrimingRemainingSeconds(primingAnchor);
  if (remaining === null) return null;
  const formatted = formatPrimingCountdown(remaining);
  const label = systemStatus?.specialMode === SPECIAL_MODE.COLLABORATIVE_SOAK
    ? COLLABORATIVE_PRIMING
    : PRIMING_SYSTEM;
  return `${label}: ${formatted} remaining`;
}

/**
 * Ordered priority chain from (systemStatus, connectionStatus, systemStatusDuration, primingAnchor)
 * to display text. Each entry's getText is tried in order; the first non-null result wins.
 * Priority order: Disconnected -> Autonomy Suppressed -> Non-Emergency Cooldown ->
 * Collaborative Defense Engaged -> Presoaking Engaged -> Priming countdown -> fallback getStatusText.
 * Consumed exclusively by getStatusDisplayText.
 */
const STATUS_DISPLAY_PRIORITY_CHAIN = [
  {
    // Disconnected: no device connection, overrides all other state
    getText: (systemStatus, connectionStatus) =>
      connectionStatus === CONNECTION_STATUS.DISCONNECTED ? getStatusText(null) : null,
  },
  {
    // Autonomy Suppressed: manual override active
    getText: (systemStatus, connectionStatus, systemStatusDuration) =>
      isAutonomySuppressed(systemStatus)
        ? getDurationRemainingText(AUTONOMY_SUPPRESSED_LABEL, systemStatusDuration)
        : null,
  },
  {
    // Non-Emergency Cooldown: collaborative response temporarily suppressed
    getText: (systemStatus, connectionStatus, systemStatusDuration) =>
      isNonEmergencyCooldownActive(systemStatus)
        ? getDurationRemainingText(NON_EMERGENCY_COOLDOWN_LABEL, systemStatusDuration)
        : null,
  },
  {
    // Collaborative Defense Engaged
    getText: (systemStatus, connectionStatus, systemStatusDuration) =>
      isCollaborativeDefenseEngaged(systemStatus)
        ? getDurationRemainingText(COLLABORATIVE_DEFENSE_LABEL, systemStatusDuration)
        : null,
  },
  {
    // Presoaking Engaged
    getText: (systemStatus, connectionStatus, systemStatusDuration) =>
      isPresoakingEngaged(systemStatus)
        ? getDurationRemainingText(PRESOAKING_LABEL, systemStatusDuration)
        : null,
  },
  {
    // Priming countdown: interpolated from the priming anchor
    getText: (systemStatus, connectionStatus, systemStatusDuration, primingAnchor) =>
      getPrimingDisplayText(systemStatus, primingAnchor),
  },
];

/**
 * Gets the appropriate status text to display based on connection and system status.
 * Walks STATUS_DISPLAY_PRIORITY_CHAIN in order and returns the first non-null match,
 * falling back to getStatusText(systemStatus) if nothing in the chain matches.
 * @param {Object|null} systemStatus - The structured system status.
 * @param {string} connectionStatus - Current connection status.
 * @param {string} systemStatusDuration - ISO 8601 duration string from the API, or null.
 * @param {{receivedAt: number, totalSeconds: number}|null} primingAnchor - Priming countdown anchor.
 * @returns {string} The resolved display text.
 */
function getStatusDisplayText(systemStatus, connectionStatus, systemStatusDuration, primingAnchor) {
  for (const { getText } of STATUS_DISPLAY_PRIORITY_CHAIN) {
    const text = getText(systemStatus, connectionStatus, systemStatusDuration, primingAnchor);
    if (text) return text;
  }
  return getStatusText(systemStatus);
}

/**
 * Helper function to get CSS class for status indicators
 * @param {string} status - The status to get class for ('good', 'warning', 'error')
 * @returns {string} The corresponding CSS class name
 */
function getStatusClass(status) {
  switch (status) {
    case HEALTH_STATUS.GOOD: return 'status-good';
    case HEALTH_STATUS.WARNING: return 'status-warning';
    case HEALTH_STATUS.ERROR: return 'status-error';
    case HEALTH_STATUS.UNKNOWN: return 'status-unknown';
    default: return 'status-unknown';
  }
}

/** Dashboard component that displays the system status and controls. */
const Dashboard = () => {
  const {
    systemStatus,
    isPresoaking,
    isActive,
    isCollaborative,
    isTestMode,
    connectionStatus,
    initiatePresoak,
    initiateActivation,
    stopPresoak,
    stopCollaboration,
    stopActivation,
    getSensorStatusFromData,
    systemStatusDuration,
    timeReported
  } = useSystem();
  const { showToast } = useToast();
  const presoak = usePresoakAction({ isPresoaking, initiatePresoak, stopPresoak, showToast });
  const activate = useActivateAction({ isActive, initiateActivation, stopActivation, showToast });
  const collaborative = useCollaborativeAction({ stopCollaboration, showToast });
  // Tick counter to force re-renders every second for live counter
  const [, setTick] = useState(0);
  // Anchor for priming countdown interpolation: derived synchronously during render so the
  // countdown is available on the first frame PRIMING state arrives, with no effect-phase gap.
  const primingAnchor = useMemo(() => {
    if (systemStatus?.sharedMode !== SHARED_MODE.PRIMING || !systemStatusDuration) return null;
    try {
      const totalSeconds = parseDurationToSeconds(systemStatusDuration);
      return totalSeconds > 0 ? { receivedAt: Date.now(), totalSeconds } : null;
    } catch (e) {
      console.error('Priming countdown: failed to parse duration', e);
      return null;
    }
  }, [systemStatusDuration, systemStatus]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Calculate on every render (triggered by tick)
  const formattedTime = timeReported ? formatTimeReported(timeReported) : DISPLAY_TEXT.NO_UPDATE;
  const stalenessClass = timeReported ? getTimeStalenessClass(timeReported) : '';
  
  const sensorStatus = getSensorStatusFromData('ember') || getSensorStatusFromData('smoke') || HEALTH_STATUS.UNKNOWN;
  // TODO: Implement water level monitoring in future iteration
  // const waterStatus = getSensorStatusFromData('water') || HEALTH_STATUS.UNKNOWN;
  // TODO: Implement signal strength monitoring in future iteration
  // const signalStatus = getSensorStatusFromData('signal') || HEALTH_STATUS.UNKNOWN;
  
  const powerStatus = connectionStatus === CONNECTION_STATUS.CONNECTED ? HEALTH_STATUS.GOOD :
                     connectionStatus === CONNECTION_STATUS.CONNECTING ? HEALTH_STATUS.WARNING : HEALTH_STATUS.ERROR;

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <div className="system-status-section">            
          <div className="status-indicators">
            <div className={`status-indicator ${getStatusClass(sensorStatus)}`}>
              <MdSensors className="indicator-icon" />
              <span className="indicator-label">Sensors</span>
            </div>

            {/* TODO: Implement water level monitoring in future iteration */}
            {/* <div className={`status-indicator ${getStatusClass(waterStatus)}`}>
              <FaTint className="indicator-icon" />
              <span className="indicator-label">Water</span>
            </div> */}

            <div className={`status-indicator ${getStatusClass(powerStatus)}`}>
              <FaPowerOff className="indicator-icon" />
              <span className="indicator-label">System</span>
            </div>

            {/* TODO: Implement signal strength monitoring in future iteration */}
            {/* <div className={`status-indicator ${getStatusClass(signalStatus)}`}>
              <FaSignal className="indicator-icon" />
              <span className="indicator-label">Signal</span>
            </div> */}
          </div>
        </div>
        
        <div className="system-status-display">
          <div className="status-text">
            <span>{getStatusDisplayText(systemStatus, connectionStatus, systemStatusDuration, primingAnchor)}</span>
          </div>
          <div className="last-updated-display">
            <span className={`last-updated-label ${stalenessClass}`}>Last updated</span>
            <span className="last-updated-time">{formattedTime}</span>
          </div>
        </div>
        
        <div className="property-visualization">
          <div className="property-image-container">
            <img
              src={getDashboardImageSource(connectionStatus, systemStatus?.sharedMode)}
              alt="AI Fire Defense system status"
              className="property-image"
            />
          </div>
        </div>
        
        <div className="control-buttons-container">
          <ActivateButton
            isActive={isActive}
            systemStatus={systemStatus}
            connectionStatus={connectionStatus}
            isTestMode={isTestMode}
            onClick={activate.handleClick}
          />
          <PresoakOrCollaborativeButton
            isCollaborative={isCollaborative}
            isPresoaking={isPresoaking}
            systemStatus={systemStatus}
            connectionStatus={connectionStatus}
            isTestMode={isTestMode}
            onPresoakClick={presoak.handleClick}
            onCollaborativeClick={collaborative.handleClick}
          />
        </div>
          
          {/* Confirmation Popups */}
          {presoak.showConfirm && (
            <ConfirmationPopup
              title={presoak.confirmTitle}
              onConfirm={presoak.handleConfirm}
              onCancel={presoak.handleCancel}
              isTestMode={isTestMode}
            />
          )}
          {activate.showConfirm && (
            <ConfirmationPopup
              title={activate.confirmTitle}
              onConfirm={activate.handleConfirm}
              onCancel={activate.handleCancel}
              isTestMode={isTestMode}
            />
          )}
          {collaborative.showConfirm && (
            <ConfirmationPopup
              title={collaborative.confirmTitle}
              onConfirm={collaborative.handleConfirm}
              onCancel={collaborative.handleCancel}
              isTestMode={isTestMode}
            />
          )}
      </div>
    </div>
  );
};

export default Dashboard;
